import { hasNoSubCategories } from '../appComponents/pages/userProfile/formFieldsSourceData';
import { hasValue } from '../lib/lib';
import { DropDownListOption } from './DropDownListOption';

export default interface Entity extends BusinessDetails {
	entityId: number;
}

export const createDefaultEntity = (profile?: Entity): Entity => {
	return { ...createBusinessDetails(profile), entityId: profile?.entityId };
};

export interface BasicEntity {
	entityId: number;
	name: string;
}

export const createDefaultBasicEntity = (entity?: BasicEntity): BasicEntity => ({
	entityId: entity?.entityId,
	name: entity?.name
});

export interface BusinessDetails {
	name: string;
	type: string;
	isTradingCompany: boolean; // TO determine if entity is personal only or business
	industry: string;
	industrySubCategory?: string;
	numberOfEmployees: number | null;
	street: string;
	suburb: string;
	postcode: string;
	country: string;
	state: string;
	phone: string;
	abn: string;
	acn: string;
	tfn: string;
	tradingName: string;
	bank: string;
	postalStreet: string;
	postalSuburb: string;
	postalCountry: string;
	postalState: string;
	postalPostcode: string;
	fax: string;
	email: string;
	website: string;
	preferredContactMethod: string;
	establishmentDate: Date;
	annualTurnover: number;
	background: string;
	isACN: boolean;
}

export const emailContactMethod = 'Email';

export const createBusinessDetails = (profile?: BusinessDetails): BusinessDetails => {
	const businessDetails = {
		name: profile?.name ?? null,
		type: profile?.type ?? null,
		isTradingCompany: profile?.isTradingCompany ?? true, // Default to true, so entity will show in Business section
		industry: profile?.industry ?? null,
		industrySubCategory: profile?.industrySubCategory,
		numberOfEmployees: profile?.numberOfEmployees ?? null,
		street: profile?.street ?? null,
		suburb: profile?.suburb ?? null,
		postcode: profile?.postcode ?? null,
		country: profile?.country ?? null,
		state: profile?.state ?? null,
		phone: profile?.phone ?? null,
		abn: profile?.abn ?? null,
		acn: profile?.acn ?? null,
		tfn: profile?.tfn ?? null,
		tradingName: profile?.tradingName ?? null,
		bank: profile?.bank ?? null,
		postalStreet: profile?.postalStreet ?? null,
		postalSuburb: profile?.postalSuburb ?? null,
		postalCountry: profile?.postalCountry ?? null,
		postalState: profile?.postalState ?? null,
		postalPostcode: profile?.postalPostcode ?? null,
		fax: profile?.fax ?? null,
		email: profile?.email,
		website: profile?.website ?? null,
		preferredContactMethod: profile?.preferredContactMethod ?? null,
		establishmentDate: profile?.establishmentDate,
		annualTurnover: profile?.annualTurnover,
		background: profile?.background ?? null,
		isACN: hasValue(profile?.acn) ?? false
	};
	// IndustrySubCategory is dependent on Industry field. Some Industry don't have subCategories, so the IndustrySubCategory
	// is hidden, so that the completion percentage will be correct.
	if (profile?.industry && hasNoSubCategories(profile?.industry)) {
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const { industrySubCategory, ...rest } = businessDetails;
		return rest;
	}
	return businessDetails;
};

export interface BusinessStructure {
	entityId: number;
	haveStructureDiagram: boolean;
	structureKeyBenefits: string;
	lifecycle: string;
	haveShareholderAgreement: boolean;
	haveBuyAndSellAgreement: boolean;
	familyBusiness: boolean;
	haveSuccessionPlanning: boolean;
	successionPlanning: string;
	intendedExitDate: Date | null;
}

export const createDefaultBusinessStructure = (item: BusinessStructure = null): BusinessStructure => {
	return {
		entityId: item?.entityId,
		haveStructureDiagram: item?.haveStructureDiagram, // Should NOT default to a value as null is radio group field option.
		structureKeyBenefits: item?.structureKeyBenefits ?? null,
		lifecycle: item?.lifecycle ?? null,
		haveShareholderAgreement: item?.haveShareholderAgreement,
		haveBuyAndSellAgreement: item?.haveBuyAndSellAgreement,
		familyBusiness: item?.familyBusiness ?? false,
		haveSuccessionPlanning: item?.haveSuccessionPlanning ?? false,
		successionPlanning: item?.successionPlanning ?? null,
		intendedExitDate: item?.intendedExitDate
	};
};

export interface BusinessFinancialInformation {
	entityid: number;
	taxReturnsAndAdviceStatus: string;
	taxReturnsAndAdviceManagedBy: string;
	businessActivityStatementStatus: string;
	businessActivityStatementManagedBy: string;
	instalmentActivityStatementStatus: string;
	instalmentActivityStatementManagedBy: string;
	financialAndManagementStatementStatus: string;
	financialAndManagementStatementManagedBy: string;
	employeeSuperannuationStatus: string;
	employeeSuperannuationManagedBy: string;
	payrollTaxStatus: string;
	payrollTaxManagedBy: string;
	fringeBenefitsTaxStatus: string;
	fringeBenefitsTaxManagedBy: string;
	workersCompensationStatus: string;
	workersCompensationManagedBy: string;
	haveInHouseAccountingTeam: boolean;
	inHouseAccountingTeamDetails: string;
	giveTimelyInformation: boolean;
	systemInUse: string;
}

export const createBusinessFinancialInformation = (
	item?: BusinessFinancialInformation
): BusinessFinancialInformation => {
	return {
		entityid: item?.entityid,
		taxReturnsAndAdviceStatus: item?.taxReturnsAndAdviceStatus ?? null,
		taxReturnsAndAdviceManagedBy: item?.taxReturnsAndAdviceManagedBy ?? null,
		businessActivityStatementStatus: item?.businessActivityStatementStatus ?? null,
		businessActivityStatementManagedBy: item?.businessActivityStatementManagedBy ?? null,
		instalmentActivityStatementStatus: item?.instalmentActivityStatementStatus ?? null,
		instalmentActivityStatementManagedBy: item?.instalmentActivityStatementManagedBy ?? null,
		financialAndManagementStatementStatus: item?.financialAndManagementStatementStatus ?? null,
		financialAndManagementStatementManagedBy: item?.financialAndManagementStatementManagedBy ?? null,
		employeeSuperannuationStatus: item?.employeeSuperannuationStatus ?? null,
		employeeSuperannuationManagedBy: item?.employeeSuperannuationManagedBy ?? null,
		payrollTaxStatus: item?.payrollTaxStatus ?? null,
		payrollTaxManagedBy: item?.payrollTaxManagedBy ?? null,
		fringeBenefitsTaxStatus: item?.fringeBenefitsTaxStatus ?? null,
		fringeBenefitsTaxManagedBy: item?.fringeBenefitsTaxManagedBy ?? null,
		workersCompensationStatus: item?.workersCompensationStatus ?? null,
		workersCompensationManagedBy: item?.workersCompensationManagedBy ?? null,
		haveInHouseAccountingTeam: item?.haveInHouseAccountingTeam ?? false,
		inHouseAccountingTeamDetails: item?.inHouseAccountingTeamDetails ?? null,
		giveTimelyInformation: item?.giveTimelyInformation ?? false,
		systemInUse: item?.systemInUse ?? null
	};
};

export interface BusinessAreasOfConcern {
	entityId: number;
	concerns: string;
	previousAccountantName: string;
	previousAccountantEmail: string;
	previousAccountantWebsite: string;
	previousAccountantSuburb: string;
	providedServicesByExistingAccountant: string;
	specificRequirements: string;
}

export const createBusinessAreasOfConcern = (item?: BusinessAreasOfConcern): BusinessAreasOfConcern => {
	return {
		entityId: item?.entityId,
		concerns: item?.concerns ?? null,
		previousAccountantName: item?.previousAccountantName ?? null,
		previousAccountantEmail: item?.previousAccountantEmail ?? null,
		previousAccountantWebsite: item?.previousAccountantWebsite ?? null,
		previousAccountantSuburb: item?.previousAccountantSuburb ?? null,
		providedServicesByExistingAccountant: item?.providedServicesByExistingAccountant ?? null,
		specificRequirements: item?.specificRequirements ?? null
	};
};

export interface BusinessVision {
	entityId: number;
	makeBusinessDifferent: boolean;
	organisationFuture: string;
	systemFuture: string;
	outcomeFuture: string;
	financialFuture: string;
}

export const createBusinessVision = (item?: BusinessVision): BusinessVision => {
	return {
		entityId: item?.entityId,
		makeBusinessDifferent: item?.makeBusinessDifferent ?? false,
		organisationFuture: item?.organisationFuture ?? null,
		systemFuture: item?.systemFuture ?? null,
		outcomeFuture: item?.outcomeFuture ?? null,
		financialFuture: item?.financialFuture ?? null
	};
};

export interface BusinessPlanningAndStrategy {
	entityId: number;
	haveBusinessPlan: boolean;
	haveBusinessCoach: boolean;
	haveSwot: boolean;
	strengths: string;
	opportunities: string;
	weakness: string;
	threats: string;
}

export const createBusinessPlanningAndStrategy = (item?: BusinessPlanningAndStrategy): BusinessPlanningAndStrategy => {
	return {
		entityId: item?.entityId,
		haveBusinessPlan: item?.haveBusinessPlan ?? false,
		haveBusinessCoach: item?.haveBusinessCoach ?? false,
		haveSwot: item?.haveSwot ?? false,
		strengths: item?.strengths ?? null,
		opportunities: item?.opportunities ?? null,
		weakness: item?.weakness ?? null,
		threats: item?.threats ?? null
	};
};

export interface BusinessRating {
	entityId: number;
	managementImportance: number;
	managementPerformance: number;
	operationImportance: number;
	operationPerformance: number;
	supportImportance: number;
	supportPerformance: number;
	marketingImportance: number;
	marketingPerformance: number;
	clientImportance: number;
	clientPerformance: number;
	financialImportance: number;
	financialPerformance: number;
	lifestyleImportance: number;
	lifestylePerformance: number;
	saleabilityImportance: number;
	saleabilityPerformance: number;
}

export const createBusinessRating = (item?: BusinessRating): BusinessRating => {
	return {
		entityId: item?.entityId ?? 0,
		managementImportance: item?.managementImportance ?? 0,
		managementPerformance: item?.managementPerformance ?? 0,
		operationImportance: item?.operationImportance ?? 0,
		operationPerformance: item?.operationPerformance ?? 0,
		supportImportance: item?.supportImportance ?? 0,
		supportPerformance: item?.supportPerformance ?? 0,
		marketingImportance: item?.marketingImportance ?? 0,
		marketingPerformance: item?.marketingPerformance ?? 0,
		clientImportance: item?.clientImportance ?? 0,
		clientPerformance: item?.clientPerformance ?? 0,
		financialImportance: item?.financialImportance ?? 0,
		financialPerformance: item?.financialPerformance ?? 0,
		lifestyleImportance: item?.lifestyleImportance ?? 0,
		lifestylePerformance: item?.lifestylePerformance ?? 0,
		saleabilityImportance: item?.saleabilityImportance ?? 0,
		saleabilityPerformance: item?.saleabilityPerformance ?? 0
	};
};

export interface EntityContact {
	entityContactId: number;
	name: string;
	email: string;
	phone: string;
	role: string;
	contactRoles: DropDownListOption<string>[];
	keyContact: boolean;
	keyDecisionMaker: boolean;
	isFamilyMember: boolean;
	entityId: number;
	percentageOwnership?: number;
}

export const createDefaultEntityContact = (item?: EntityContact): EntityContact => {
	return {
		entityContactId: item?.entityContactId,
		name: item?.name ?? null,
		email: item?.email,
		phone: item?.phone ?? null,
		role: item?.role ?? '',
		contactRoles: item?.contactRoles ?? [],
		keyContact: item?.keyContact ?? false,
		keyDecisionMaker: item?.keyDecisionMaker ?? false,
		isFamilyMember: item?.isFamilyMember ?? false,
		entityId: item?.entityId,
		percentageOwnership: item?.percentageOwnership ?? null
	};
};

export interface EntityAssociation {
	entityAssociationId: number;
	associatedEntityName: string;
	role: string;
	associationRoles: DropDownListOption<string>[];
	entityId: number;
	percentageOwnership?: number;
}

export const createDefaultEntityAssociation = (item?: EntityAssociation): EntityAssociation => {
	return {
		entityAssociationId: item?.entityAssociationId,
		associatedEntityName: item?.associatedEntityName ?? null,
		role: item?.role ?? null,
		associationRoles: item?.associationRoles ?? [],
		entityId: item?.entityId,
		percentageOwnership: item?.percentageOwnership ?? null
	};
};

export interface EntityGoal {
	entityGoalId: number;
	year: string;
	goal: string;
	description: string;
	entityId: number;
}

export const createDefaultEntityGoal = (item?: EntityGoal): EntityGoal => {
	return {
		entityGoalId: item?.entityGoalId,
		year: item?.year ?? null,
		goal: item?.goal ?? null,
		description: item?.description ?? '',
		entityId: item?.entityId
	};
};

export interface EntityDocumentsInformation {
	hasLastYearTaxReturnsDocuments: boolean | null;
	hasLastYearFinancialStatementsDocuments: boolean | null;
	hasRecentActivityStatementDocuments: boolean | null;
	hasChequeAndSavingsAccountsDocuments: boolean | null;
	hasCashManagementTrustsDocuments: boolean | null;
	hasAmountOutstandingDocuments: boolean | null;
	hasRecentBusinessPlanDocuments: boolean | null;
	hasBusinessValuationReportDocuments: boolean | null;
	hasCompanyStructureDocuments: boolean | null;
	hasHumanResourcesStructureDocuments: boolean | null;
	hasUnitTrustCertificatesDocuments: boolean | null;
	hasTermDepositAndBondDocuments: boolean | null;
	hasShareCertificatesDocuments: boolean | null;
	hasMotorVehicleInsuranceDocuments: boolean | null;
	hasBusinessInsuranceDocuments: boolean | null;
	hasOtherInsuranceDocuments: boolean | null;
	otherEntityFinancialInformation: string;
	businessValuation: number | null;
}

export const createDefaultEntityDocumentsInformation = (
	item?: EntityDocumentsInformation
): EntityDocumentsInformation => {
	return {
		hasLastYearTaxReturnsDocuments: item?.hasLastYearTaxReturnsDocuments, // Should NOT default to a value as null is radio group field option
		hasLastYearFinancialStatementsDocuments: item?.hasLastYearFinancialStatementsDocuments,
		hasRecentActivityStatementDocuments: item?.hasRecentActivityStatementDocuments,
		hasChequeAndSavingsAccountsDocuments: item?.hasChequeAndSavingsAccountsDocuments,
		hasCashManagementTrustsDocuments: item?.hasCashManagementTrustsDocuments,
		hasAmountOutstandingDocuments: item?.hasAmountOutstandingDocuments,
		hasRecentBusinessPlanDocuments: item?.hasRecentBusinessPlanDocuments,
		hasBusinessValuationReportDocuments: item?.hasBusinessValuationReportDocuments,
		hasCompanyStructureDocuments: item?.hasCompanyStructureDocuments,
		hasHumanResourcesStructureDocuments: item?.hasHumanResourcesStructureDocuments,
		hasUnitTrustCertificatesDocuments: item?.hasUnitTrustCertificatesDocuments,
		hasTermDepositAndBondDocuments: item?.hasTermDepositAndBondDocuments,
		hasShareCertificatesDocuments: item?.hasShareCertificatesDocuments,
		hasMotorVehicleInsuranceDocuments: item?.hasMotorVehicleInsuranceDocuments,
		hasBusinessInsuranceDocuments: item?.hasBusinessInsuranceDocuments,
		hasOtherInsuranceDocuments: item?.hasOtherInsuranceDocuments,
		otherEntityFinancialInformation: item?.otherEntityFinancialInformation,
		businessValuation: item?.businessValuation ?? null
	};
};
